import { Select, Button, message, Modal, Input } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RecipesTable from "./RecipesTable";
import { getAdminRecipes, deleteRecipe } from "../../services/recipe.service";
import api from "../../services/axios";

import "./recipes.css";

export default function RecipesPage() {
  const navigate = useNavigate();

  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [cuisine, setCuisine] = useState(undefined);
  const [status, setStatus] = useState(undefined);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRecipes = useCallback(
    async (pageNumber = 1, instantSearch = debouncedSearch) => {
      try {
        setLoading(true);
        const res = await getAdminRecipes({
          page: pageNumber,
          limit: pageSize,
          search: instantSearch,
          cuisineType: cuisine,
          status,
        });
        setRecipes(res?.data || []);
        setTotal(res?.total || 0);
        if (pageNumber > 1 && (res?.data || []).length === 0) {
          setPage(1);
          fetchRecipes(1, instantSearch);
        } else {
          setPage(pageNumber);
        }
      } catch {
        message.error("Failed to load recipes");
      } finally {
        setLoading(false);
      }
    },
    [pageSize, debouncedSearch, cuisine, status]
  );

  useEffect(() => { fetchRecipes(1); }, [fetchRecipes]);
  useEffect(() => { fetchRecipes(1); }, [debouncedSearch, cuisine, status, fetchRecipes]);

  const updateRecipeLocal = useCallback((id, changes) => {
    setRecipes((prev) => prev.map((r) => (r._id === id ? { ...r, ...changes } : r)));
  }, []);

  const handleToggleDisable = useCallback(
    async (id, value) => {
      try {
        updateRecipeLocal(id, { isDisabled: value });
        await api.patch(`/admin/recipes/${id}/disable`, { value });
        message.success("Recipe status updated");
      } catch {
        message.error("Failed to update status");
        fetchRecipes(page);
      }
    },
    [updateRecipeLocal, fetchRecipes, page]
  );

  const handleToggleTrending = useCallback(
    async (id, value) => {
      try {
        updateRecipeLocal(id, { isTrending: value });
        await api.patch(`/admin/recipes/${id}/trending`, { value });
        message.success("Trending status updated");
      } catch {
        message.error("Failed to update trending");
        fetchRecipes(page);
      }
    },
    [updateRecipeLocal, fetchRecipes, page]
  );

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        centered: true,
        icon: null,
        width: 420,
        title: (
          <div style={{ textAlign: "center", fontSize: 22, fontWeight: 600, color: "#ef4444" }}>
            Delete
          </div>
        ),
        content: (
          <div style={{ textAlign: "center", fontSize: 14, color: "#6b7280", marginTop: 8 }}>
            Are you sure you want to delete{" "}
            <span style={{ fontWeight: 600, color: "#111827" }}>"{record.title}"</span>?
          </div>
        ),
        footer: (
          <div style={{ display: "flex", justifyContent: "center", gap: 14, marginTop: 18 }}>
            <Button onClick={() => Modal.destroyAll()}>No</Button>
            <Button
              danger
              type="primary"
              onClick={async () => {
                try {
                  await deleteRecipe(record._id);
                  message.success("Recipe deleted");
                  Modal.destroyAll();
                  fetchRecipes(page);
                } catch {
                  message.error("Failed to delete recipe");
                }
              }}
            >
              Delete
            </Button>
          </div>
        ),
      });
    },
    [fetchRecipes, page]
  );

  const handleEdit = useCallback(
    (record) => navigate(`/dashboard/recipes/edit/${record._id}`),
    [navigate]
  );

  const handleReset = () => {
    setSearch("");
    setCuisine(undefined);
    setStatus(undefined);
    setDebouncedSearch("");
    fetchRecipes(1, "");
  };

  const handleSearchEnter = (e) => {
    const value = e.target.value;
    setDebouncedSearch(value);
    fetchRecipes(1, value);
  };

  return (
    <div className="recipes-page">
      {/* Title — mirrors "Total Users (06)" */}
      <div className="recipes-heading">
        Total Recipes ({total.toString().padStart(2, "0")})
      </div>

      {/* Filter bar — mirrors Users filter row exactly */}
      <div className="recipes-filter-bar">
        <div className="recipes-filter-left">
          <span className="recipes-filter-label">Filters</span>
          <Input
            placeholder="Search recipes"
            className="recipes-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleSearchEnter}
          />
        </div>

        <div className="recipes-filter-right">
          <Select
            placeholder="Cuisine"
            className="recipes-select"
            popupClassName="recipes-select-popup"
            allowClear
            value={cuisine}
            onChange={(val) => setCuisine(val)}
            options={[
              { value: "Chinese", label: "Chinese" },
              { value: "Indian", label: "Indian" },
              { value: "Middle Eastern", label: "Middle Eastern" },
              { value: "Italian", label: "Italian" },
              { value: "american", label: "American" },
              { value: "Continental", label: "Continental" },
              { value: "French", label: "French" },
              { value: "Japanese", label: "Japanese" },
              { value: "Korean", label: "Korean" },
              { value: "Greek", label: "Greek" },
              { value: "Mexican", label: "Mexican" },
              { value: "Turkish", label: "Turkish" },
            ]}
          />

          <Select
            placeholder="Status"
            className="recipes-select"
            popupClassName="recipes-select-popup"
            allowClear
            value={status}
            onChange={(val) => setStatus(val)}
            options={[
              { value: "live", label: "Live" },
              { value: "disabled", label: "Disabled" },
              { value: "pending_review", label: "Pending" },
            ]}
          />

          <Button
            type="link"
            icon={<ReloadOutlined />}
            className="recipes-reset-btn"
            onClick={handleReset}
          >
            Reset
          </Button>

          <Button
            icon={<PlusOutlined />}
            className="new-recipe-btn"
            type="primary"
            onClick={() => navigate("/dashboard/recipes/new")}
          >
            New Recipe
          </Button>
        </div>
      </div>

      {/* Table — wrapped in same border/radius container as Users */}
      <div className="recipes-table-wrapper">
        <RecipesTable
          recipes={recipes}
          loading={loading}
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={fetchRecipes}
          onToggleDisable={handleToggleDisable}
          onToggleTrending={handleToggleTrending}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}